import { useState } from "react";
import { Select, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { PillTabs } from "@/shared/components/PillTabs";
import { EmptyState } from "@/shared/components/EmptyState";
import { useQuery } from "@tanstack/react-query";
import { formatRelativeTime } from "@/shared/lib/formatRelativeTime";
import { ISSUE_LABELS } from "@/features/patient/medications/components/medicationReviewBadges";
import { outcomeActionLabel } from "@/features/patient/medications/components/reviewOutcomeLabels";
import { formatDate } from "@/features/admin/clinicians/components/formatDate";
import { assignedItemsQuery, cliniciansQuery } from "@/portals/admin/api/adminQueries";
import type { AdminAssignedItem, AdminItemState } from "@/portals/admin/api/adminReviewQueueApi";

type Filter = "ALL" | AdminItemState;

const STATE_LABEL: Record<AdminItemState, { label: string; className: string }> = {
  IN_QUEUE: { label: "In queue", className: "bg-[#EEF2F7] text-[#0A2947]" },
  WITH_CLINICIAN: { label: "With clinician", className: "bg-[#FEF6DC] text-[#946200]" },
  CLOSED: { label: "Closed", className: "bg-[#DCFCE7] text-[#16A34A]" },
};

function outcomeText(outcome: string | null): string {
  if (!outcome) return "—";
  if (outcome === "PATIENT_NO_LONGER_TAKES") return "Patient no longer takes it";
  return outcomeActionLabel(outcome as Parameters<typeof outcomeActionLabel>[0], false);
}

const columns: ColumnsType<AdminAssignedItem> = [
  {
    title: "Medicine",
    key: "medicine",
    render: (_, i) => (
      <div>
        <p className="!mb-0 text-sm font-extrabold text-[#0A2947]">{i.medicineName || (i.triggerType === "GOLDEN_RECORD_CONFLICT" ? "Conflicting records" : "Unnamed medicine")}</p>
        <p className="!mb-0 text-xs text-[#6B7280]">{ISSUE_LABELS[i.triggerType]}</p>
      </div>
    ),
  },
  { title: "Patient", dataIndex: "patientDisplayName", key: "patient" },
  {
    title: "Status",
    key: "state",
    render: (_, i) => (
      <div className="flex flex-wrap items-center gap-1.5">
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${STATE_LABEL[i.state].className}`}>{STATE_LABEL[i.state].label}</span>
        {i.awaitingPatientReply && (
          <span className="rounded-full bg-[#FFF2F4] px-2 py-0.5 text-[11px] font-bold text-[#E4002B]">Waiting for patient</span>
        )}
      </div>
    ),
  },
  {
    title: "Clinician",
    key: "clinician",
    render: (_, i) =>
      i.clinician ? (
        <div>
          <p className="!mb-0 text-sm font-semibold text-[#0A2947]">{i.clinician.name}</p>
          <p className="!mb-0 text-xs text-[#6B7280]">
            {i.attachedVia === "ASSIGNED_BY_ADMIN" ? `Assigned${i.assignedByAdmin ? ` by ${i.assignedByAdmin}` : ""}` : "Claimed"}
          </p>
        </div>
      ) : (
        <span className="text-xs text-[#9CA3AF]">Unassigned</span>
      ),
  },
  {
    title: "Claimed / assigned",
    key: "attachedAt",
    render: (_, i) =>
      i.attachedAt ? (
        <span title={new Date(i.attachedAt).toLocaleString()}>
          {formatDate(i.attachedAt)}, {new Date(i.attachedAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
        </span>
      ) : (
        "—"
      ),
  },
  { title: "Last activity", key: "lastActivityAt", render: (_, i) => formatRelativeTime(i.lastActivityAt) },
  {
    title: "Outcome",
    key: "outcome",
    render: (_, i) => (
      <div>
        <p className="!mb-0 text-sm text-[#0A2947]">{outcomeText(i.outcome)}</p>
        {i.resolvedAt && <p className="!mb-0 text-xs text-[#6B7280]">{formatDate(i.resolvedAt)}</p>}
      </div>
    ),
  },
];

/**
 * Admin → Assigned Items: every medication review that left the patient's
 * hands — in the queue, with a clinician (claimed or assigned, when, by whom,
 * waiting for the patient?) or closed (with the outcome). Live, and a row
 * opens the task (view, Reassign, Return to queue).
 */
export function AssignedItemsView({ onOpenItem }: { onOpenItem: (id: string) => void }) {
  const [filter, setFilter] = useState<Filter>("ALL");
  const [clinicianId, setClinicianId] = useState<string | undefined>(undefined);

  // One cache entry per filter; live via useAdminLiveQueries.
  const query = useQuery(assignedItemsQuery(filter === "ALL" ? undefined : filter, clinicianId));
  const clinicians = useQuery(cliniciansQuery());
  const items = query.data ?? [];

  return (
    <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
      <div className="rounded-3xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
        {/* Pill filter centred like every other splitter; the clinician picker sits on the
            right (three columns: spacer · pills · picker). Stacked and centred on narrow screens. */}
        <div className="mb-5 flex flex-col items-center gap-3 md:grid md:grid-cols-[1fr_auto_1fr]">
          <span aria-hidden className="hidden md:block" />
          <PillTabs
            label="Assigned items filter"
            active={filter}
            onChange={setFilter}
            tabs={[
              { id: "ALL", label: "All" },
              { id: "IN_QUEUE", label: "In queue" },
              { id: "WITH_CLINICIAN", label: "With clinician" },
              { id: "CLOSED", label: "Closed" },
            ]}
          />
          <Select
            aria-label="Filter by clinician"
            allowClear
            placeholder="All clinicians"
            className="!w-56 md:justify-self-end"
            value={clinicianId}
            onChange={(v) => setClinicianId(v || undefined)}
            options={(clinicians.data ?? []).map((c) => ({
              value: c.userId,
              label: `${c.firstName} ${c.lastName}`.trim() || c.email,
            }))}
          />
        </div>
        <Table<AdminAssignedItem>
          rowKey="id"
          columns={columns}
          dataSource={items}
          loading={query.isPending}
          pagination={{ pageSize: 15, hideOnSinglePage: true }}
          locale={{ emptyText: <EmptyState variant="compact" title="No items here." /> }}
          scroll={{ x: 1000 }}
          rowClassName="cursor-pointer"
          onRow={(item) => ({
            onClick: () => onOpenItem(item.id),
            onKeyDown: (e) => { if (e.key === "Enter") onOpenItem(item.id); },
            tabIndex: 0,
            "aria-label": `Open ${item.medicineName || "item"}`,
          })}
        />
      </div>
    </div>
  );
}
